import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Table,
  message,
  Tag,
  Select,
  Space,
  Typography,
  Drawer,
  Descriptions,
} from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import {
  listMetaFlowSubmissions,
  getMetaFlowSubmission,
  listLocalMetaFlows,
} from 'services/metaFlowService';

const { Text } = Typography;
const { Option } = Select;

const STATUS_COLORS = {
  completed: 'success',
  in_progress: 'processing',
};

const formatAnswerPreview = (preview) => {
  if (!preview || typeof preview !== 'object') {
    return '—';
  }
  const entries = Object.entries(preview);
  if (entries.length === 0) {
    return '—';
  }
  return entries.map(([key, value]) => `${key}: ${value}`).join(' · ');
};

const MetaFlowResponses = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [flows, setFlows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    listLocalMetaFlows()
      .then((data) => setFlows(data?.data || []))
      .catch(() => setFlows([]));
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        status: statusFilter,
      };
      if (flowFilter !== 'all') {
        params.meta_flow_id = flowFilter;
      }
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const data = await listMetaFlowSubmissions(params);
      setRows(data?.data || []);
      setTotal(data?.meta?.total || 0);
    } catch (error) {
      console.error('Failed to load flow responses:', error);
      message.error('Failed to load flow responses');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, statusFilter, flowFilter, searchText]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await getMetaFlowSubmission(id);
      setDetail(data?.data || null);
    } catch (error) {
      message.error('Failed to load response details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
    },
    {
      title: 'Flow',
      key: 'flow',
      render: (_, record) => (
        <div>
          <Text strong>{record.meta_flow_name || '—'}</Text>
          {record.meta_flow_id ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.meta_flow_id}
              </Text>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'WhatsApp',
      dataIndex: 'wa_id',
      width: 140,
      render: (value) => value || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {(status || 'unknown').replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Last screen',
      dataIndex: 'last_screen',
      width: 120,
      render: (value) => value || '—',
    },
    {
      title: 'Answers',
      key: 'answers',
      ellipsis: true,
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatAnswerPreview(record.answer_preview)}
        </Text>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => openDetail(record.id)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, marginBottom: 20 }}
      animate={{ opacity: 1, marginBottom: 0 }}
    >
      <Card title="MetaFlow Responses">
        <Row gutter={ROW_GUTTER} className="mb-3" align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search phone, token, flow id…"
              value={searchText}
              onChange={(e) => {
                setPage(1);
                setSearchText(e.target.value);
              }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setPage(1);
                setStatusFilter(value);
              }}
            >
              <Option value="all">All statuses</Option>
              <Option value="completed">Completed</Option>
              <Option value="in_progress">In progress</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Select
              style={{ width: '100%' }}
              value={flowFilter}
              onChange={(value) => {
                setPage(1);
                setFlowFilter(value);
              }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All flows</Option>
              {flows.map((flow) => (
                <Option key={flow.meta_flow_id} value={flow.meta_flow_id}>
                  {flow.name || flow.meta_flow_id}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 960 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPerPage(nextSize);
            },
          }}
        />
      </Card>

      <Drawer
        title={detail ? `Response #${detail.id}` : 'Response'}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={520}
        destroyOnClose
      >
        {detailLoading ? (
          <Text type="secondary">Loading…</Text>
        ) : detail ? (
          <>
            <Descriptions column={1} size="small" bordered className="mb-3">
              <Descriptions.Item label="Flow">
                {detail.meta_flow_name || '—'}
                {detail.meta_flow_id ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {detail.meta_flow_id}
                    </Text>
                  </div>
                ) : null}
              </Descriptions.Item>
              <Descriptions.Item label="WhatsApp">{detail.wa_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[detail.status] || 'default'}>
                  {(detail.status || 'unknown').replace('_', ' ')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Last screen">
                {detail.last_screen || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Flow token">
                <Text copyable style={{ fontSize: 12 }}>
                  {detail.flow_token || '—'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Report ID">
                {detail.report_id || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {detail.updated_at
                  ? new Date(detail.updated_at).toLocaleString()
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Text strong>Answers</Text>
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 6,
                fontSize: 12,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(detail.response_json || {}, null, 2)}
            </pre>
          </>
        ) : (
          <Text type="secondary">No data</Text>
        )}
      </Drawer>
    </motion.div>
  );
};

export default MetaFlowResponses;
